import type { components } from "@brandex/api-client";
import type { Locale, MessageKey } from "@brandex/i18n";
import { Alert, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Spinner, cn } from "@brandex/ui";
import { useQuery } from "@tanstack/react-query";
import { Info, MousePointerClick, ShieldCheck, UserRound, Users, Eye } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { useCurrentOrg } from "../client-scope";
import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";

type Stats = components["schemas"]["SiteStatsDto"];
type Day = components["schemas"]["DayStatsDto"];

/** `YYYY-MM-DD` of a day in Bangladesh. */
export function dhakaToday(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dhaka", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** Moves a `YYYY-MM-DD` day by whole days (calendar arithmetic in UTC, no time zone surprises). */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const PRESETS = [
  { key: "analytics.range.7", days: 7 },
  { key: "analytics.range.30", days: 30 },
  { key: "analytics.range.90", days: 90 },
  { key: "analytics.range.365", days: 365 },
] as const;

export interface Range {
  from: string;
  to: string;
}

/** The last `days` days up to today. */
export function lastDays(days: number, today: string): Range {
  return { from: addDays(today, -(days - 1)), to: today };
}

/** Patients who used a booking button per 100 visitors, rounded; `null` without visitors. */
export function bookingRate(s: Pick<Stats, "bookers" | "visitors">): number | null {
  return s.visitors === 0 ? null : Math.round((s.bookers / s.visitors) * 1000) / 10;
}

/** Whether a custom range is complete and the right way round (the server allows up to 400 days). */
export function validRange(r: Range): boolean {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(r.from) || !re.test(r.to) || r.from > r.to) return false;
  return (Date.parse(`${r.to}T00:00:00Z`) - Date.parse(`${r.from}T00:00:00Z`)) / 86_400_000 < 400;
}

const keys = {
  stats: (orgId: string, r: Range) => ["orgs", orgId, "analytics", r.from, r.to] as const,
};

/**
 * Website visitors (F-33): visits, visitors and booking clicks on the
 * doctor's site for a date range. Used by the doctor and, inside a
 * `ClientScope`, by BrandEx staff.
 */
export function SiteStatsView() {
  const t = useT();
  const org = useCurrentOrg();
  if (org.isPending) return <Spinner label={t("common.loading")} />;
  if (org.isError || org.orgId === undefined) return <Alert tone="error">{errorMessage(org.error, t)}</Alert>;
  return <Stats orgId={org.orgId} />;
}

function Stats({ orgId }: { orgId: string }) {
  const t = useT();
  const { api, locale } = useBrandex();
  const [today] = useState(() => dhakaToday());
  const [preset, setPreset] = useState<number | "custom">(30);
  const [range, setRange] = useState<Range>(() => lastDays(30, today));
  const [draft, setDraft] = useState<Range>(range);
  const stats = useQuery<Stats>({
    queryKey: keys.stats(orgId, range),
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/analytics", { params: { path: { org_id: orgId }, query: range } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });

  const choose = (days: number) => {
    setPreset(days);
    const r = lastDays(days, today);
    setRange(r);
    setDraft(r);
  };
  const s = stats.data;
  const rate = s ? bookingRate(s) : null;

  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("analytics.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("analytics.subtitle")}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div role="group" aria-label={t("analytics.range")} className="flex flex-wrap gap-1 rounded-[var(--radius-pill)] border border-border p-1">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              aria-pressed={preset === p.days}
              onClick={() => choose(p.days)}
              className={cn(
                "rounded-[var(--radius-pill)] px-3 py-1.5 text-sm font-medium",
                preset === p.days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(p.key)}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={preset === "custom"}
            onClick={() => setPreset("custom")}
            className={cn(
              "rounded-[var(--radius-pill)] px-3 py-1.5 text-sm font-medium",
              preset === "custom" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t("analytics.range.custom")}
          </button>
        </div>
        {preset === "custom" ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (validRange(draft)) setRange(draft);
            }}
          >
            <Field label={t("analytics.from")}>
              <Input type="date" max={today} value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </Field>
            <Field label={t("analytics.to")}>
              <Input type="date" max={today} value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </Field>
            <button type="submit" disabled={!validRange(draft)} className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
              {t("analytics.apply")}
            </button>
          </form>
        ) : null}
        {stats.isFetching && s ? <Spinner label={t("common.loading")} /> : null}
      </div>
      {preset === "custom" && !validRange(draft) ? <p className="text-sm text-destructive">{t("analytics.badRange")}</p> : null}

      {stats.isPending ? <Spinner label={t("common.loading")} /> : null}
      {stats.isError ? <Alert tone="error">{errorMessage(stats.error, t)}</Alert> : null}
      {s ? (
        <>
          <p className="text-sm text-muted-foreground">{t("analytics.showing", { from: dayLabel(locale, s.from, true), to: dayLabel(locale, s.to, true) })}</p>
          <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile icon={<Eye className="h-4 w-4" aria-hidden />} label={t("analytics.visits")} value={s.visits} hint={t("analytics.visitsHint")} locale={locale} />
            <Tile icon={<Users className="h-4 w-4" aria-hidden />} label={t("analytics.visitors")} value={s.visitors} hint={t("analytics.visitorsHint")} locale={locale} />
            <Tile icon={<MousePointerClick className="h-4 w-4" aria-hidden />} label={t("analytics.clicks")} value={s.booking_clicks} hint={t("analytics.clicksHint")} locale={locale} />
            <Tile
              icon={<UserRound className="h-4 w-4" aria-hidden />}
              label={t("analytics.bookers")}
              value={s.bookers}
              hint={rate === null ? t("analytics.bookersHint") : t("analytics.bookersRate", { rate: rate.toLocaleString(locale === "bn" ? "bn-BD" : "en-GB") })}
              locale={locale}
            />
          </dl>

          <Card>
            <CardHeader>
              <CardTitle>{t("analytics.daily")}</CardTitle>
              <CardDescription>{t("analytics.dailyHint")}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              {s.visits === 0 && s.booking_clicks === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{t("analytics.empty")}</p> : <DailyChart days={s.daily} locale={locale} />}
              <details className="text-sm">
                <summary className="cursor-pointer font-medium">{t("analytics.table")}</summary>
                <div className="mt-3 max-h-80 overflow-auto">
                  <table className="w-full text-left tabular-nums">
                    <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
                      <tr>
                        <th className="py-1.5 pr-3 font-medium">{t("analytics.day")}</th>
                        <th className="py-1.5 pr-3 font-medium">{t("analytics.visits")}</th>
                        <th className="py-1.5 pr-3 font-medium">{t("analytics.visitors")}</th>
                        <th className="py-1.5 font-medium">{t("analytics.clicks")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...s.daily].reverse().map((d) => (
                        <tr key={d.date} className="border-t border-border">
                          <td className="py-1.5 pr-3">{dayLabel(locale, d.date, true)}</td>
                          <td className="py-1.5 pr-3">{num(locale, d.visits)}</td>
                          <td className="py-1.5 pr-3">{num(locale, d.visitors)}</td>
                          <td className="py-1.5">{num(locale, d.booking_clicks)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Breakdown
              title={t("analytics.channels")}
              rows={s.by_channel.map((c) => ({ name: t(`analytics.channel.${c.channel}` as MessageKey), count: c.count }))}
              empty={t("analytics.noClicks")}
              locale={locale}
            />
            <Breakdown
              title={t("analytics.sources")}
              rows={[
                ...s.referrers.map((r) => ({ name: r.name, count: r.count })),
                { name: t("analytics.direct"), count: Math.max(0, s.visits - s.referrers.reduce((n, r) => n + r.count, 0)) },
              ]}
              empty={t("analytics.noVisits")}
              locale={locale}
            />
            <Breakdown
              title={t("analytics.devices")}
              rows={s.devices.map((d) => ({ name: t(`analytics.device.${d.device}` as MessageKey), count: d.count }))}
              empty={t("analytics.noVisits")}
              locale={locale}
            />
            <Breakdown title={t("analytics.pages")} rows={s.pages.map((p) => ({ name: p.name, count: p.count }))} empty={t("analytics.noVisits")} locale={locale} mono />
          </div>

          <p className="flex gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("analytics.approximate")}
          </p>
          <p className="flex gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("analytics.privacy")}
          </p>
        </>
      ) : null}
    </div>
  );
}

const num = (locale: Locale, n: number) => n.toLocaleString(locale === "bn" ? "bn-BD" : "en-GB");

function dayLabel(locale: Locale, day: string, year = false): string {
  return new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    ...(year ? { year: "numeric" } : { weekday: "short" }),
  }).format(new Date(`${day}T00:00:00Z`));
}

function Tile({ icon, label, value, hint, locale }: { icon: ReactNode; label: string; value: number; hint: string; locale: Locale }) {
  return (
    <div className="grid gap-1 rounded-[var(--radius-lg)] border border-border bg-card p-4">
      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </dt>
      <dd className="font-display text-3xl font-bold tabular-nums">{num(locale, value)}</dd>
      <dd className="text-xs text-muted-foreground">{hint}</dd>
    </div>
  );
}

/**
 * Visits per day as bars (one series, so no legend: the card title names
 * it). Every bar is focusable and shows that day's numbers on hover or focus.
 */
function DailyChart({ days, locale }: { days: Day[]; locale: Locale }) {
  const t = useT();
  const tip = useId();
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(1, ...days.map((d) => d.visits));
  const shown = active === null ? undefined : days[active];
  // Axis labels: first, middle and last day.
  const ticks = [0, Math.floor((days.length - 1) / 2), days.length - 1].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <span>{t("analytics.max", { n: num(locale, max) })}</span>
        <span aria-live="polite" id={tip} className="min-h-4 text-right text-foreground">
          {shown
            ? `${dayLabel(locale, shown.date)} · ${t("analytics.tip", { visits: num(locale, shown.visits), visitors: num(locale, shown.visitors), clicks: num(locale, shown.booking_clicks) })}`
            : null}
        </span>
      </div>
      <div className="relative h-44 border-b border-border" onMouseLeave={() => setActive(null)}>
        <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-border/70" />
        <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-border/70" />
        <ul className={cn("relative flex h-full items-end", days.length > 120 ? "gap-0" : days.length > 45 ? "gap-px" : "gap-0.5")} aria-label={t("analytics.daily")}>
          {days.map((d, i) => (
            <li key={d.date} className="flex h-full min-w-0 flex-1 items-end">
              <button
                type="button"
                aria-describedby={active === i ? tip : undefined}
                aria-label={`${dayLabel(locale, d.date)}: ${t("analytics.tip", { visits: num(locale, d.visits), visitors: num(locale, d.visitors), clicks: num(locale, d.booking_clicks) })}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                className="group flex h-full w-full items-end focus-visible:outline-2 focus-visible:outline-offset-1"
              >
                <span
                  className={cn("block w-full rounded-t-[4px] transition-colors", active === i ? "bg-primary" : "bg-primary/70 group-hover:bg-primary", d.visits === 0 && "bg-border")}
                  style={{ height: d.visits === 0 ? "2px" : `${Math.max(3, (d.visits / max) * 100)}%` }}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative h-4 text-xs text-muted-foreground" aria-hidden>
        {ticks.map((i) => (
          <span
            key={i}
            className="absolute -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full"
            style={{ left: `${days.length === 1 ? 0 : (i / (days.length - 1)) * 100}%` }}
          >
            {days[i] ? dayLabel(locale, days[i].date) : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

function Breakdown({ title, rows, empty, locale, mono = false }: { title: string; rows: { name: string; count: number }[]; empty: string; locale: Locale; mono?: boolean }) {
  const list = rows.filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...list.map((r) => r.count));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="grid gap-2.5">
            {list.map((r) => (
              <li key={r.name} className="grid gap-1">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className={cn("truncate", mono && "font-mono text-xs")}>{r.name}</span>
                  <span className="tabular-nums text-muted-foreground">{num(locale, r.count)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                  <div className="h-full rounded-full bg-primary/70" style={{ width: `${(r.count / max) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
